/**
 * `HudDraw` primitives on a device-resolution canvas (03 §8, 09 §7).
 *
 * Callers work in integer stage px. Every primitive maps to device px itself: rect edges round to
 * device px (a rule is never thinner than 1 device px); text runs are rasterized at a whole number
 * `n` of device px per glyph px (`snap`, core/hud/type.ts) and drawn 1:1 at a whole device-px
 * position, never scaled.
 *
 * `ctx` keeps the stage transform `setTransform(k, 0, 0, k, −ox, −oy)` for custom path drawing.
 * Primitives switch to the identity transform internally and restore it.
 */
import type { HudDraw, RGB, TextOpts } from '../api.ts';
import { type Anchor, fontText, maskWidth, type PixelFont, type PixelFonts, type TextBox, placeText, pyRound, upper } from './font.ts';
import { type CanvasFactory, type HudCanvas, TextCache, ctx2d, defaultCanvasFactory, packRgb } from './text-cache.ts';
import { glyphHeight, tierRun, type TextSize } from './type.ts';

export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const cssCache = new Map<number, string>();
/** `rgb(r,g,b)` string, cached per colour (allocation-free after the first use). */
export function cssRgb(c: RGB): string {
  const key = packRgb(c);
  let s = cssCache.get(key);
  if (s === undefined) {
    s = `rgb(${String(c[0] & 255)},${String(c[1] & 255)},${String(c[2] & 255)})`;
    cssCache.set(key, s);
  }
  return s;
}

/** Stage px → device px for one coordinate. */
export function toDevice(v: number, k: number): number {
  return Math.round(v * k);
}

export class DrawImpl implements HudDraw {
  ctx: CanvasRenderingContext2D;
  /** device px per stage px */
  k = 1;
  /** device-px origin of the target canvas (0 for the main HUD canvas; panel origin for caches) */
  ox = 0;
  oy = 0;
  readonly fonts: PixelFonts;
  readonly cache: TextCache;
  private readonly box: TextBox = { x: 0, y: 0, w: 0, h: 0 };
  /** the font and raster size `resolve` picked for the current run */
  private rf: PixelFont;
  private rn = 1;

  constructor(ctx: Ctx2D, fonts: PixelFonts, cache: TextCache) {
    // OffscreenCanvasRenderingContext2D has the same drawing API; only `.canvas` differs.
    this.ctx = ctx as CanvasRenderingContext2D;
    this.fonts = fonts;
    this.cache = cache;
    this.rf = fonts['5x7'];
  }

  /** Point at a target: device scale and origin; sets the stage transform. */
  bind(ctx: Ctx2D, k: number, ox = 0, oy = 0): void {
    this.ctx = ctx as CanvasRenderingContext2D;
    this.k = k;
    this.ox = ox;
    this.oy = oy;
    this.stageTransform();
  }

  stageTransform(): void {
    this.ctx.setTransform(this.k, 0, 0, this.k, -this.ox, -this.oy);
    this.ctx.imageSmoothingEnabled = false;
  }

  dx(v: number): number {
    return toDevice(v, this.k) - this.ox;
  }
  dy(v: number): number {
    return toDevice(v, this.k) - this.oy;
  }

  /** Pick the font and `n` (device px per glyph px) of a text call into `rf` / `rn`; no tier = S. */
  private resolve(opts: TextOpts | undefined): void {
    const r = tierRun(opts?.tier ?? 'S', this.k);
    this.rf = this.fonts[r.font];
    this.rn = r.n;
  }

  textHeight(tier: TextSize): number {
    return glyphHeight(tier, this.k) / this.k;
  }

  /** Width in stage px of the snapped run (`glyph px · n / f`, not an integer). */
  textWidth(str: string, opts?: TextOpts): number {
    this.resolve(opts);
    return maskWidth(this.rf, str, this.rn) / this.k;
  }

  /** v6 `text()`: returns the mask width in stage px. `opts.cached === false` → glyph-by-glyph. */
  text(x: number, y: number, str: string, rgb: RGB, opts?: TextOpts): number {
    this.resolve(opts);
    const font = this.rf;
    const n = this.rn;
    const anchor: Anchor = opts?.anchor ?? 'l';
    const shadow = opts?.shadow ?? null;
    // the anchor rounds in stage px (as v6; keeps the ×u identity), the shift is a whole number of
    // device px, so right/centre-anchored runs of different lengths end on the same device px at fractional f
    const b = placeText(this.box, font, str, x, y, n / this.k, 'l');
    if (str.length === 0) return b.w;
    const wDev = maskWidth(font, str, n);
    const bx = this.dx(pyRound(x)) - (anchor === 'r' ? wDev : anchor === 'c' ? Math.round(Math.floor(wDev / this.k / 2) * this.k) : 0);
    const ctx = this.ctx;
    const ga = ctx.globalAlpha;
    if (opts?.alpha !== undefined) ctx.globalAlpha = ga * opts.alpha;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (opts?.cached === false) this.glyphs(font, str, b, bx, n, packRgb(rgb), shadow ? packRgb(shadow) : -1);
    else this.run(font, str, b, bx, n, rgb, shadow);
    ctx.globalAlpha = ga;
    this.stageTransform();
    return b.w;
  }

  private run(font: PixelFont, str: string, b: TextBox, bx: number, n: number, rgb: RGB, shadow: RGB | null): void {
    const r = this.cache.run(font, str, n, rgb, shadow);
    if (r.w === 0 || r.h === 0) return;
    const ctx = this.ctx;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(r.canvas, 0, 0, r.w, r.h, bx - r.ox, this.dy(b.y) - r.oy, r.w, r.h);
  }

  /**
   * Glyph-by-glyph from a tinted atlas: no per-string cache entry (fast-changing numbers).
   * Expects upper-case text (ASCII lower case is folded; other characters fall back per glyph).
   * The shadow is the same 3×3 dilation as v6, drawn as 9 offset passes under the text.
   */
  private glyphs(font: PixelFont, text: string, b: TextBox, bx: number, n: number, rgb: number, sh: number): void {
    const str = fontText(font, upper(text));
    this.ctx.imageSmoothingEnabled = false;
    if (sh >= 0) {
      const at = this.cache.glyphAtlas(font, sh, n);
      for (let oy = -n; oy <= n; oy += n) {
        for (let ox = -n; ox <= n; ox += n) this.glyphPass(font, str, b, bx, n, at.canvas, ox, oy);
      }
    }
    this.glyphPass(font, str, b, bx, n, this.cache.glyphAtlas(font, rgb, n).canvas, 0, 0);
  }

  /** One pass over the glyphs; (offX, offY) in device px. */
  private glyphPass(font: PixelFont, s: string, b: TextBox, bx: number, n: number, src: HudCanvas, offX: number, offY: number): void {
    const ctx = this.ctx;
    let cx = 0;
    const x0 = bx + offX;
    const y = this.dy(b.y) + offY;
    for (let i = 0; i < s.length; i++) {
      let cp = s.codePointAt(i) ?? 32;
      if (cp > 0xffff) i++;
      if (cp >= 97 && cp <= 122) cp -= 32; // ASCII fast path of str.upper()
      const g = font.glyph(cp) ?? font.glyph(String.fromCodePoint(cp).toUpperCase().codePointAt(0) ?? 32);
      if (g) ctx.drawImage(src, g.x * n, g.y * n, g.w * n, g.h * n, x0 + cx * n, y, g.w * n, g.h * n);
      cx += (g ? g.advance : font.fallbackAdvance) + font.spacing;
    }
  }

  rect(x: number, y: number, w: number, h: number, rgb: RGB, alpha?: number): void {
    if (w <= 0 || h <= 0) return;
    const ctx = this.ctx;
    const x0 = this.dx(x);
    const y0 = this.dy(y);
    const x1 = Math.max(this.dx(x + w), x0 + 1);
    const y1 = Math.max(this.dy(y + h), y0 + 1);
    const ga = ctx.globalAlpha;
    if (alpha !== undefined) ctx.globalAlpha = ga * alpha;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = cssRgb(rgb);
    ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
    ctx.globalAlpha = ga;
    this.stageTransform();
  }

  /** 1 stage px horizontal rule from (x, y), w stage px long. */
  hline(x: number, y: number, w: number, rgb: RGB, alpha?: number): void {
    this.rect(x, y, w, 1, rgb, alpha);
  }

  /** 1 stage px vertical rule from (x, y), h stage px long. */
  vline(x: number, y: number, h: number, rgb: RGB, alpha?: number): void {
    this.rect(x, y, 1, h, rgb, alpha);
  }

  /** Make a rect fully transparent (a "hole" through which the GL map shows). */
  hole(x: number, y: number, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    const x0 = this.dx(x);
    const y0 = this.dy(y);
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(x0, y0, this.dx(x + w) - x0, this.dy(y + h) - y0);
    this.stageTransform();
  }

  /**
   * Draw a stage-px source (1 source px = 1 stage px) at stage (x, y), scaled ×k (smoothed unless
   * k is an integer). `w`/`h` default to the source size. The `putImageData` helper
   * (09 §12): fill a `StagePixelBuffer`, `commit()`, then `blit(buf.canvas, …)`.
   */
  blit(src: CanvasImageSource & { width: number; height: number }, x: number, y: number, w?: number, h?: number): void {
    const sw = w ?? src.width;
    const sh = h ?? src.height;
    if (sw <= 0 || sh <= 0) return;
    const ctx = this.ctx;
    const x0 = this.dx(x);
    const y0 = this.dy(y);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = !Number.isInteger(this.k);
    if (!Number.isInteger(this.k)) ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, sw, sh, x0, y0, this.dx(x + sw) - x0, this.dy(y + sh) - y0);
    this.stageTransform();
  }
}

/**
 * Stage-px pixel buffer for `putImageData`-style panels (09 §12): `putImageData` ignores
 * `setTransform`, so fill `data` (RGBA, stage px), `commit()`, then `draw.blit(buf.canvas, x, y)`.
 */
export class StagePixelBuffer {
  readonly w: number;
  readonly h: number;
  readonly canvas: HudCanvas;
  readonly image: ImageData;
  readonly data: Uint8ClampedArray;
  private readonly ctx: Ctx2D;

  constructor(w: number, h: number, createCanvas: CanvasFactory = defaultCanvasFactory) {
    this.w = w;
    this.h = h;
    this.canvas = createCanvas(w, h);
    const ctx = ctx2d(this.canvas);
    this.ctx = ctx;
    this.image = ctx.createImageData(w, h);
    this.data = this.image.data;
  }

  commit(): void {
    this.ctx.putImageData(this.image, 0, 0);
  }
}
